import { useState, useEffect } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RefreshCw, Loader2, HelpCircle } from "lucide-react";

// URL del Backend
const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
const API = `${API_URL}/api`;

const CustomCaptcha = ({ onVerified, onError, isOpen }) => {
  // Estados para el captcha de TEXTO
  const [captchaId, setCaptchaId] = useState(null);
  const [captchaQuestion, setCaptchaQuestion] = useState(""); // <-- De imagen a texto
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(true);
  const [errorCount, setErrorCount] = useState(0);

  // Regenerar captcha cuando el diálogo se abre
  useEffect(() => {
    if (isOpen) {
      // Cuando se abre, cargar nuevo captcha
      loadCaptcha();
    } else {
      // Cuando se cierra, resetear todo el estado
      setCaptchaId(null);
      setCaptchaQuestion("");
      setAnswer("");
      setLoading(true);
    }
  }, [isOpen]);

  // Función para cargar un nuevo captcha
  const loadCaptcha = async () => {
    setLoading(true);
    setCaptchaQuestion("");
    setAnswer(""); // Limpiar la respuesta anterior
    try {
      // Agregar timestamp para evitar caché
      const timestamp = Date.now();
      const response = await axios.get(`${API}/captcha/generate?t=${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      // Leemos 'question' (texto) en lugar de 'image' (imagen)
      const { captcha_id, question } = response.data;
      
      if (!captcha_id || !question) {
        throw new Error("Error en el servidor al generar captcha");
      }
      
      setCaptchaId(captcha_id);
      setCaptchaQuestion(question); // <-- Guardamos la pregunta de texto
    } catch (err) {
      console.error("Error loading captcha:", err);
      if (onError) {
        onError("No se pudo cargar la verificación. Intenta de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Función para manejar el envío
  const handleSubmit = (e) => {
    e.preventDefault(); // Prevenimos el refresco de la página
    if (!answer.trim() || !captchaId) return;

    // Convertimos la respuesta a número, como espera el backend
    const numericAnswer = parseInt(answer, 10);

    if (isNaN(numericAnswer)) {
      if(onError) onError("Por favor, introduce solo números.");
      return;
    }

    // Llamamos a 'onVerified' con el OBJETO que espera App.js
    onVerified({
      captcha_id: captchaId,
      captcha_answer: numericAnswer, // Enviamos la respuesta como número
    });
  };

  // Esta función se llama desde App.js si el backend rechaza el captcha
  // (La mantenemos por si la necesitas en el futuro, aunque ahora App.js
  // maneja el error directamente)
  const reloadOnExternalError = () => {
    setErrorCount(c => c + 1);
    loadCaptcha();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* AQUÍ ESTÁ EL CAMBIO:
        Quitamos la <img> y ponemos un <p> para mostrar la pregunta de texto.
      */}
      <div className="flex justify-center items-center p-4 bg-gray-100 dark:bg-gray-800 rounded-md min-h-[90px]">
        {loading && <Loader2 className="w-8 h-8 animate-spin text-gray-500" />}
        
        {!loading && captchaQuestion && (
          <div className="text-center">
            <HelpCircle className="w-6 h-6 text-teal-500 mx-auto mb-2" />
            <p 
              className="text-lg font-medium text-gray-900 dark:text-gray-100"
              data-testid="captcha-question"
            >
              {captchaQuestion}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              (Escribe solo el resultado en números)
            </p>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <Input
          type="number" // <-- Cambiado a 'number' para facilitar
          placeholder="Escribe el resultado"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          required
          autoFocus
          className="flex-grow"
          autoComplete="off"
          inputMode="numeric" // <-- Ayuda en móviles
        />
        <Button 
          type="button" 
          variant="outline" 
          size="icon" 
          onClick={loadCaptcha} 
          disabled={loading}
          title="Recargar captcha"
          data-testid="reload-captcha"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <Button 
        type="submit" 
        className="w-full bg-teal-600 hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-600 dark:text-black" 
        disabled={!answer.trim() || loading}
        data-testid="submit-captcha"
      >
        Verificar y Enviar Mensaje
      </Button>
    </form>
  );
};

export default CustomCaptcha;